import React, { useState, useEffect } from 'react';
import { formatDateForLocal } from '../utils/dateUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API_URL = process.env.REACT_APP_API_URL;

const ReportsView = () => {
  const [reportType, setReportType] = useState('citas');
  const [dateRange, setDateRange] = useState({
    start: formatDateForLocal(new Date()),
    end: formatDateForLocal(new Date())
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/reportes/${reportType}?start=${dateRange.start}&end=${dateRange.end}`);
        const json = await res.json();
        setData(json || []);
      } catch (err) {
        console.error('Error al cargar reporte:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [reportType, dateRange]);

  const generarPDF = () => {
    const doc = new jsPDF();
    doc.text(`Reporte de ${reportType.toUpperCase()}`, 14, 14);

    if (data.length > 0) {
      const headers = Object.keys(data[0])
        .filter(key => !['_id', '__v', 'createdAt', 'updatedAt'].includes(key));
      const body = data.map(item => headers.map(h => String(item[h] ?? '')));

      doc.autoTable({
        head: [headers],
        body
      });
    } else {
      doc.text('No hay datos disponibles para este reporte.', 14, 30);
    }

    doc.save(`reporte_${reportType}_${dateRange.start}_a_${dateRange.end}.pdf`);
  };

  const formatField = (key, value) => {
    if (key === 'tieneConvenio') {
      return (
        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {value ? 'Sí' : 'No'}
        </span>
      );
    }
    if (key === 'genero') {
      return value === 'Femenino' ? '♀ Femenino' : '♂ Masculino';
    }
    if (key === 'fechaNacimiento' && value) {
      return new Date(value).toLocaleDateString('es-CL');
    }
    if (key === 'estado') {
      const colors = {
        pendiente: 'bg-yellow-100 text-yellow-700',
        confirmada: 'bg-blue-100 text-blue-700',
        completada: 'bg-green-100 text-green-700',
        cancelada: 'bg-red-100 text-red-700'
      };
      return <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${colors[value] || 'bg-gray-100 text-gray-700'}`}>{value}</span>;
    }
    return String(value);
  };

  const renderHeaders = () => {
    if (data.length === 0) return null;
    return Object.keys(data[0])
      .filter(key => !['_id', '__v', 'createdAt', 'updatedAt'].includes(key))
      .map((key, i) => (
        <th key={i} className="px-4 py-2 text-left capitalize text-xs text-gray-600">
          {key === 'rut' ? 'RUT' :
           key === 'tieneConvenio' ? 'Convenio' :
           key === 'fechaNacimiento' ? 'F. Nacimiento' :
           key === 'promocionDescuento' ? 'Descuento' :
           key.charAt(0).toUpperCase() + key.slice(1)}
        </th>
      ));
  };

  const renderRows = () => {
    if (data.length === 0) return null;
    return data.map((item, i) => (
      <tr key={i} className="hover:bg-gray-50 transition">
        {Object.entries(item)
          .filter(([key]) => !['_id', '__v', 'createdAt', 'updatedAt'].includes(key))
          .map(([key, value], j) => (
            <td key={j} className="px-4 py-2 text-sm text-gray-800">
              {formatField(key, value)}
            </td>
          ))}
      </tr>
    ));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-semibold mb-6">📊 Reportes y Estadísticas</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Reporte</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="citas">Citas</option>
            <option value="ingresos">Ingresos</option>
            <option value="procedimientos">Procedimientos</option>
            <option value="pacientes">Pacientes</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
          <input
            type="date"
            name="start"
            value={dateRange.start}
            onChange={handleDateChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
          <input
            type="date"
            name="end"
            value={dateRange.end}
            onChange={handleDateChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-auto">
        {loading ? (
          <p className="text-center text-sm text-gray-500">Cargando datos...</p>
        ) : data.length === 0 ? (
          <p className="text-center text-sm text-gray-400">Sin resultados para este rango.</p>
        ) : (
          <table className="min-w-full text-sm text-gray-700">
            <thead className="bg-indigo-100 text-indigo-800">
              <tr>{renderHeaders()}</tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {renderRows()}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-end mt-6">
      </div>
    </div>
  );
};

export default ReportsView;
